// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { materialsForYear } from '../data';
import { catInfo } from '../domain';
import { squarify } from '../utils/squarify';
import { rankedBarsSvg, rateColour } from '../components/charts';
import { attachSvgZoom } from '../components/svgZoom';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatTonnes, formatPercent, fyShort } from '../utils/format';

type ColourMode = 'recovery' | 'material';

export function renderMaterials(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  let year = d.latestYear;
  let colourMode: ColourMode = 'recovery';

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Which materials actually get recycled</h1>
      <p class="view-sub">Every rectangle is a material, sized by how much we throw out and shaded by its
        ${glossaryTerm('recovery-rate', 'recovery rate')} — green is well recovered, red is landfilled. The biggest
        pile (building rubble) is also well recovered; the alarming one is plastics: small, and almost all red.</p>
    </div>
    <div class="controls">
      <span class="field-label">Year</span>
      <select data-role="year">
        ${[...d.years].reverse().map((yy) => `<option value="${yy}"${yy === year ? ' selected' : ''}>${escapeHtml(fyShort(yy))}</option>`).join('')}
      </select>
      <span class="field-label">Shade by</span>
      <div class="seg" role="group" data-role="colour">
        <button class="seg-btn" data-c="recovery" aria-pressed="true">Recovery rate</button>
        <button class="seg-btn" data-c="material" aria-pressed="false">Material</button>
      </div>
    </div>
    <div class="grid-2">
      <div class="panel">
        <div class="panel-title">Materials by volume &amp; recovery</div>
        <div class="panel-sub" data-role="tsub"></div>
        <div class="treemap-frame" data-role="treemap"></div>
        <div class="map-legend" data-role="tlegend"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Recovery-rate leaderboard</div>
        <div class="panel-sub">Share of each material kept out of landfill. Click a bar for detail.</div>
        <div data-role="leader"></div>
      </div>
    </div>
  `;

  const treeEl = root.querySelector('[data-role="treemap"]') as HTMLElement;
  const leaderEl = root.querySelector('[data-role="leader"]') as HTMLElement;
  const tsub = root.querySelector('[data-role="tsub"]') as HTMLElement;
  const tlegend = root.querySelector('[data-role="tlegend"]') as HTMLElement;
  let zoom: { destroy(): void } | null = null;

  function build(): void {
    const mats = materialsForYear(d, year);
    tsub.textContent = `${fyShort(year)} · ${mats.length} materials · shaded by ${colourMode === 'recovery' ? 'recovery rate' : 'material type'}`;

    // treemap
    const W = 600, H = 430;
    const rects = squarify(mats.map((m) => m.total), W, H);
    const tiles = rects
      .map((r, i) => {
        const m = mats[i];
        const fill = colourMode === 'recovery' ? rateColour(m.rate) : catInfo(m.key).colour;
        const showLabel = r.w > 66 && r.h > 30;
        const showRate = r.w > 66 && r.h > 48;
        const label = showLabel
          ? `<text x="${r.x + 6}" y="${r.y + 16}" style="font-size:11px;font-weight:600;fill:#fff;paint-order:stroke;stroke:rgba(0,0,0,0.25);stroke-width:2px">${escapeHtml(catInfo(m.key).short)}</text>` +
            (showRate ? `<text x="${r.x + 6}" y="${r.y + 31}" style="font-size:10px;fill:#fff;paint-order:stroke;stroke:rgba(0,0,0,0.2);stroke-width:2px">${formatPercent(m.rate, 0)} recovered</text>` : '')
          : '';
        return `<g data-open-material="${escapeHtml(m.key)}" role="button" data-tip="${escapeHtml(`${catInfo(m.key).short}\n${formatTonnes(m.total)} · ${formatPercent(m.rate, 0)} recovered`)}">
          <rect x="${r.x}" y="${r.y}" width="${Math.max(0, r.w - 1)}" height="${Math.max(0, r.h - 1)}" rx="2" fill="${fill}"></rect>${label}</g>`;
      })
      .join('');
    zoom?.destroy();
    treeEl.innerHTML = `<svg class="chart treemap" viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="xMinYMin meet" role="img">${tiles}</svg>`;
    const svg = treeEl.querySelector('svg') as SVGSVGElement;
    zoom = attachSvgZoom(svg, { maxScale: 6 });
    svg.querySelectorAll('[data-open-material]').forEach((el) => el.addEventListener('click', () => ctx.openMaterial(el.getAttribute('data-open-material') as string)));

    tlegend.innerHTML = colourMode === 'recovery'
      ? `<span class="field-label">Recovery rate</span><span class="scale-bar">${['#c0503f', '#d1713a', '#e0902d', '#a9a13a', '#5f9647', '#2f8f4e'].map((c) => `<span class="scale-swatch" style="background:${c}"></span>`).join('')}</span><span style="font-family:var(--font-mono);font-size:var(--font-size-xs)">0% · 100%</span>`
      : `<span class="field-label">Size = tonnes; colours are material categories</span>`;

    // leaderboard by recovery rate
    const ranked = [...mats].sort((a, b) => b.rate - a.rate);
    leaderEl.innerHTML = rankedBarsSvg(
      ranked.map((m) => ({
        label: catInfo(m.key).short,
        value: m.rate,
        colour: rateColour(m.rate),
        tip: `${catInfo(m.key).short}: ${formatPercent(m.rate, 1)} recovered\n${formatTonnes(m.total)} total`,
        data: { 'open-material': m.key },
      })),
      { width: 560, valueFmt: (v) => `${v.toFixed(0)}%`, clickAttr: 'open-material' },
    );
    leaderEl.querySelectorAll('[data-open-material]').forEach((el) => el.addEventListener('click', () => ctx.openMaterial(el.getAttribute('data-open-material') as string)));
  }

  root.querySelector('[data-role="year"]')?.addEventListener('change', (e) => { year = (e.target as HTMLSelectElement).value; build(); });
  root.querySelector('[data-role="colour"]')?.addEventListener('click', (e) => {
    const b = (e.target as Element).closest('[data-c]'); if (!b) return;
    colourMode = b.getAttribute('data-c') as ColourMode;
    root.querySelectorAll('[data-c]').forEach((x) => x.setAttribute('aria-pressed', String(x.getAttribute('data-c') === colourMode)));
    build();
  });

  build();
}
